import type { LedgerMethod } from '@carsfinders/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

import { t } from '../../../lib/i18n';
import {
  DEPOSITS_QUERY_KEY,
  FINANCE_CLIENTS_QUERY_KEY,
  createDirectTopUp,
  financeErrorMessage,
  refundDeposit,
  refusalCode,
  remindDepositTopUp,
  type DepositsOverviewRow,
} from '../api';
import { trapTabKey, useReturnFocusOnUnmount } from '../dialog-focus';
import { SHOW_CARD_METHOD } from '../topup-flags';
import { cn, formatChf, formatDayMonth, formatNumber } from '../utils';

/** Method options the direct top-up form's select offers — `card` stays out pending (q). */
const DIRECT_TOPUP_METHODS: LedgerMethod[] = SHOW_CARD_METHOD ? ['bank', 'card', 'cash'] : ['bank', 'cash'];

/* ─────────────────────────────── View model ──────────────────────────────── */

/** What the row's one action does — artboard `depRows[].act`. */
export type DepositAction = 'remind' | 'refund' | 'mode';

/** What an action left behind on the row, kept by the table across refetches. */
export interface DepositDoneMark {
  kind: 'reminded' | 'refunded';
  at: Date;
}

/** The grid every header and body row of the table shares. */
export const DEPOSITS_GRID =
  'grid min-w-[980px] grid-cols-[minmax(0,1.3fr)_140px_110px_minmax(0,1.6fr)_150px_170px] items-center gap-3.5 px-[18px]';

/**
 * The «Дія» column — header and body cell alike — pinned to the right edge of
 * the scrolling table (BUG-17). `self-stretch` makes the cell as tall as the
 * row, so its opaque background covers whatever scrolls underneath.
 */
export const DEPOSITS_ACTION_CELL = 'sticky right-0 z-[1] self-stretch bg-cf-surface';

/**
 * One action per row: a client blocked by the ceiling or missing a deposit is
 * reminded to top up; a pending refund request is paid out; everyone else gets
 * a link to their bidding mode. A refund request with nothing to refund falls
 * back to the link — the server would refuse a zero refund anyway.
 *
 * A TRUSTED client is never reminded to top up (BUG-29): their limit is a bid
 * count, not a deposit, so money would not unblock them. A trusted row at its
 * limit links to the bidding-mode card instead, where the limit is changed.
 */
export function depositAction(row: DepositsOverviewRow): DepositAction {
  if (row.state === 'refund_requested' && row.depositChf > 0) return 'refund';
  if (row.mode === 'trusted') return 'mode';
  if (row.state === 'over_ceiling' || row.state === 'awaiting_deposit') return 'remind';
  return 'mode';
}

/**
 * The deposit is FROZEN while a live bid still stands on it (owner,
 * 2026-10-12): an active bid or money committed in one. The server refuses the
 * refund then (409 `REFUND_NOT_ALLOWED` — client-deposit.service.ts, which also
 * counts unpaid invoices the row does not carry), so the button says so up
 * front instead of failing after a confirm.
 */
export function refundFrozen(row: DepositsOverviewRow): boolean {
  return row.activeBidCount > 0 || row.committedChf > 0;
}

/** «депозит ×10» / «довірений · 10» / «без режиму». */
export function depositModeLabel(row: DepositsOverviewRow): string {
  if (row.mode === 'deposit' && row.multiplier !== null) {
    return t('adminFinance.deposits.mode.deposit', { multiplier: row.multiplier });
  }
  if (row.mode === 'trusted' && row.bidLimit !== null) {
    return t('adminFinance.deposits.mode.trusted', { limit: row.bidLimit });
  }
  return t('adminFinance.deposits.mode.legacy');
}

/** The deposit cell: a trusted client holding nothing prints absence, not `CHF 0`. */
export function depositCell(row: DepositsOverviewRow): string {
  return row.mode === 'trusted' && row.depositChf <= 0 ? '—' : formatChf(row.depositChf);
}

export interface DepositUsage {
  /** 0..100 — the bar width. */
  pct: number;
  text: string;
}

/**
 * The «Використано» bar and its caption. Committed against the cap in deposit
 * mode, active bids against the limit in trusted mode — both straight off the
 * server's row; the bar is clamped, the caption is not (an over-ceiling client
 * reads the true committed figure).
 */
export function depositUsage(row: DepositsOverviewRow): DepositUsage {
  let pct = 0;
  let text: string;
  if (row.mode === 'deposit') {
    const cap = row.capChf ?? 0;
    if (row.depositChf <= 0 || cap <= 0) {
      text = t('adminFinance.deposits.used.blocked');
    } else {
      pct = Math.min(100, Math.round((row.committedChf / cap) * 100));
      text = t('adminFinance.deposits.used.deposit', {
        committed: formatNumber(row.committedChf),
        cap: formatNumber(cap),
      });
    }
  } else if (row.mode === 'trusted') {
    const limit = row.bidLimit ?? 0;
    pct = limit > 0 ? Math.min(100, Math.round((row.activeBidCount / limit) * 100)) : 100;
    text = t('adminFinance.deposits.used.trusted', { active: row.activeBidCount, limit });
  } else {
    text = t('adminFinance.deposits.used.legacy', { committed: formatNumber(row.committedChf) });
  }
  if (row.state === 'refund_requested' && row.refundRequestedAt) {
    text = t('adminFinance.deposits.used.refund', { date: formatDayMonth(row.refundRequestedAt) });
  }
  return { pct: Math.max(0, pct), text };
}

const STATE_PILL: Record<string, string> = {
  can_bid: 'bg-cf-success-tint text-cf-success',
  over_ceiling: 'bg-cf-danger-tint text-cf-danger',
  refund_requested: 'bg-cf-info-tint text-cf-info-ink',
  awaiting_deposit: 'bg-cf-fill text-cf-ink-muted',
  legacy: 'bg-cf-fill text-cf-ink-muted',
};

/** `11:32` — when an action was taken, in the operator's clock. */
function formatTime(date: Date): string {
  return new Intl.DateTimeFormat('uk-UA', { hour: '2-digit', minute: '2-digit' }).format(date);
}

/** A failed action, as an EXPLANATION where the server gave a typed reason. */
function actionError(error: unknown, action: 'remind' | 'refund'): { text: string; info: boolean } {
  const code = refusalCode(error);
  if (code === 'PAYMENTS_DISABLED') {
    return { text: t('adminFinance.deposits.error.paymentsDisabled'), info: true };
  }
  if (code === 'REFUND_NOT_ALLOWED') {
    return { text: t('adminFinance.deposits.error.refundNotAllowed'), info: false };
  }
  return {
    text:
      financeErrorMessage(error) ??
      t(
        action === 'remind'
          ? 'adminFinance.deposits.error.remindFailed'
          : 'adminFinance.deposits.error.refundFailed',
      ),
    info: false,
  };
}

/* ─────────────────────────────── Component ───────────────────────────────── */

interface DepositRowProps {
  row: DepositsOverviewRow;
  /** «Роздріб» / «Дилер» — from the balances rollup; null when the client has no row there. */
  role: string | null;
  done: DepositDoneMark | undefined;
  onDone: (userId: string, mark: DepositDoneMark) => void;
}

/**
 * One client on «Депозити й режими» — artboard `depRows`.
 *
 * Client · mode · deposit · usage bar · state pill · ONE action. The state is
 * the server's `buyingPowerState` verbatim; nothing here re-derives who may
 * bid. The actions write for real: «Нагадати поповнити» queues a reminder and
 * «Повернути» records the refund of the whole deposit, behind an explicit
 * confirm. After either, the row says what was done instead of offering it
 * again, and focus lands back on the row.
 */
export function DepositRow({ row, role, done, onDone }: DepositRowProps) {
  const queryClient = useQueryClient();
  const rowRef = useRef<HTMLDivElement>(null);
  const actionRef = useRef<HTMLButtonElement>(null);
  const [confirming, setConfirming] = useState(false);
  const [toppingUp, setToppingUp] = useState(false);
  /**
   * Where focus goes after the confirm closes or an action settles — applied
   * AFTER the commit, because the element it targets may only just have
   * (re)appeared: the row once the action button is replaced by «нагадано …»,
   * the button once the dialog is gone.
   */
  const [focusTarget, setFocusTarget] = useState<'row' | 'action' | null>(null);
  useEffect(() => {
    if (!focusTarget) return;
    (focusTarget === 'row' ? rowRef.current : actionRef.current)?.focus();
    setFocusTarget(null);
  }, [focusTarget]);

  const who = row.name ?? row.email ?? row.userId;
  const caption = role ?? (row.name ? row.email : null) ?? '—';
  const action = depositAction(row);
  const usage = depositUsage(row);
  const stateKey = row.state ?? 'legacy';
  const refundAmount = formatChf(row.depositChf);

  const remind = useMutation({
    mutationFn: () => remindDepositTopUp(row.userId),
    onSuccess: () => {
      onDone(row.userId, { kind: 'reminded', at: new Date() });
      setFocusTarget('row');
    },
  });

  const refund = useMutation({
    mutationFn: () => refundDeposit(row.userId),
    onSuccess: () => {
      onDone(row.userId, { kind: 'refunded', at: new Date() });
      // The deposit, the held-deposits figure and the journal all moved.
      void queryClient.invalidateQueries({ queryKey: DEPOSITS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: FINANCE_CLIENTS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'finance', 'summary'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'finance', 'journal'] });
      setFocusTarget('row');
    },
  });

  const pending = remind.isPending || refund.isPending;
  const frozen = action === 'refund' && refundFrozen(row);
  const failure = remind.error
    ? actionError(remind.error, 'remind')
    : refund.error
      ? actionError(refund.error, 'refund')
      : null;

  const closeConfirm = () => {
    setConfirming(false);
    setFocusTarget('action');
  };

  return (
    <div
      ref={rowRef}
      role="row"
      tabIndex={-1}
      data-testid={`deposit-row-${row.userId}`}
      className={cn(DEPOSITS_GRID, 'border-b border-cf-line-soft py-3 text-[13px] outline-none')}
    >
      <div role="cell" className="min-w-0">
        <Link
          to="/clients/$id"
          params={{ id: row.userId }}
          className="block min-w-0 text-left hover:underline"
        >
          {/* BUG-17: both lines truncate in the 1.3fr column — the hint keeps them whole. */}
          <div className="truncate font-semibold" title={who}>
            {who}
          </div>
          <div className="truncate text-[12px] text-cf-ink-subtle" title={caption}>
            {caption}
          </div>
        </Link>
      </div>

      <span
        role="cell"
        className={cn(
          'font-mono text-[12px]',
          row.mode === 'trusted'
            ? 'text-cf-ok'
            : row.mode === 'deposit'
              ? 'text-cf-ink-muted'
              : 'text-cf-ink-subtle',
        )}
      >
        {depositModeLabel(row)}
      </span>

      <span role="cell" className="text-right font-mono">
        {depositCell(row)}
      </span>

      <div role="cell" className="flex min-w-0 flex-col gap-[5px]">
        <div
          role="meter"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={usage.pct}
          aria-label={t('adminFinance.deposits.usedAria', { pct: usage.pct })}
          className="h-1.5 overflow-hidden rounded-[3px] bg-cf-fill"
        >
          <div
            className={cn(
              'h-full',
              row.state === 'over_ceiling' ? 'bg-cf-danger' : 'bg-cf-ink-faint',
            )}
            style={{ width: `${usage.pct}%` }}
          />
        </div>
        <span className="truncate font-mono text-[11px] text-cf-ink-subtle">{usage.text}</span>
      </div>

      <div role="cell">
        <span
          className={cn(
            'inline-flex w-fit rounded-cf-sm px-2 py-1 font-mono text-[10px] tracking-[0.08em] uppercase',
            STATE_PILL[stateKey],
          )}
        >
          {t(`adminFinance.deposits.state.${stateKey}`)}
        </span>
      </div>

      {/*
        BUG-17: the table scrolls sideways below its 980px floor (the admin
        sidebar leaves ~1000px at 1200px wide), which used to push this column
        past the right edge. It sticks to the scrollport instead — opaque and
        stretched to the row's height, so the columns it slides over never show
        through — while the middle columns keep scrolling.
      */}
      <div
        role="cell"
        className={cn(
          DEPOSITS_ACTION_CELL,
          'flex flex-col items-end justify-center gap-1 text-right',
        )}
      >
        {/*
          A manual top-up is offered on EVERY row, independent of `action` /
          `done`: unlike «нагадати» / «повернути» it is not tied to a
          particular buying-power state — an operator can record money that
          arrived by phone or in person for any client at any time. Kept as a
          quiet secondary link so it never competes with the row's one primary
          action, and placed ABOVE it (owner, 2026-10-12: «Поповнити вручну»
          on top, «Повернути …» below).
        */}
        <button
          type="button"
          onClick={() => setToppingUp(true)}
          className="text-[11px] font-semibold text-cf-ink-subtle underline decoration-dotted underline-offset-2 hover:text-cf-ink"
        >
          {t('adminFinance.deposits.action.topUp')}
        </button>
        {done ? (
          <span className="font-mono text-[11px] text-cf-success">
            {done.kind === 'refunded'
              ? t('adminFinance.deposits.done.refunded')
              : t('adminFinance.deposits.done.reminded', { time: formatTime(done.at) })}
          </span>
        ) : action === 'mode' ? (
          <Link
            to="/clients/$id"
            params={{ id: row.userId }}
            aria-label={t('adminFinance.deposits.action.modeAria', { name: who })}
            className="inline-flex h-8 items-center rounded-cf-md border border-cf-line bg-cf-surface px-3 text-[12px] font-semibold whitespace-nowrap text-cf-ink hover:border-cf-ink"
          >
            {t('adminFinance.deposits.action.mode')}
          </Link>
        ) : (
          <button
            ref={actionRef}
            type="button"
            // aria-disabled, not disabled: focus parks here while the call is in
            // flight, and a disabled button cannot hold focus.
            aria-disabled={pending || frozen}
            data-testid="deposit-row-action"
            title={
              action === 'remind'
                ? t('adminFinance.deposits.action.remindHint')
                : frozen
                  ? t('adminFinance.deposits.error.refundNotAllowed')
                  : undefined
            }
            onClick={() => {
              if (pending || frozen) return;
              if (action === 'remind') {
                refund.reset();
                remind.mutate();
              } else {
                remind.reset();
                setConfirming(true);
              }
            }}
            className={cn(
              'h-8 rounded-cf-md border px-3 text-[12px] font-semibold whitespace-nowrap aria-disabled:opacity-40',
              frozen ? 'aria-disabled:cursor-not-allowed' : 'aria-disabled:cursor-wait',
              action === 'refund'
                ? 'border-cf-brand bg-cf-brand text-white hover:bg-cf-brand-hover'
                : 'border-cf-line bg-cf-surface text-cf-ink hover:border-cf-ink',
            )}
          >
            {pending
              ? t('adminFinance.deposits.action.pending')
              : action === 'refund'
                ? t('adminFinance.deposits.action.refund', { amount: refundAmount })
                : t('adminFinance.deposits.action.remind')}
          </button>
        )}
        {frozen && !done && (
          <span data-testid="deposit-refund-frozen" className="max-w-[190px] text-[11px] leading-[1.4] text-cf-ink-subtle">
            {t('adminFinance.deposits.action.refundFrozen', {
              count: row.activeBidCount,
              amount: formatChf(row.committedChf),
            })}
          </span>
        )}
        {failure && !done && (
          <p
            role="alert"
            className={cn(
              'max-w-[170px] text-left text-[11px] leading-[1.4]',
              failure.info ? 'text-cf-info-ink' : 'text-cf-danger',
            )}
          >
            {failure.text}
          </p>
        )}
      </div>

      {toppingUp && (
        <DirectTopUpDialog userId={row.userId} name={who} onClose={() => setToppingUp(false)} />
      )}

      {confirming && (
        <RefundConfirmDialog
          name={who}
          amount={refundAmount}
          onCancel={closeConfirm}
          onConfirm={() => {
            setConfirming(false);
            // Focus parks on the (pending) button, then moves to the row on
            // success or stays on the button on a refusal.
            setFocusTarget('action');
            refund.mutate(undefined, { onError: () => setFocusTarget('action') });
          }}
        />
      )}
    </div>
  );
}

/**
 * The explicit confirm a refund needs. An `alertdialog`: focus starts on
 * «Скасувати» (the safe choice for an irreversible booking), Tab stays inside,
 * Escape and the backdrop cancel.
 */
function RefundConfirmDialog({
  name,
  amount,
  onCancel,
  onConfirm,
}: {
  name: string;
  amount: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const titleId = useId();
  const bodyId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onCancel();
      return;
    }
    if (event.key === 'Tab') {
      event.preventDefault();
      const next =
        document.activeElement === cancelRef.current ? confirmRef.current : cancelRef.current;
      next?.focus();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-cf-ink/35 p-4 backdrop-blur-[2px]"
      onClick={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        onKeyDown={onKeyDown}
        className="flex w-full max-w-[440px] flex-col gap-3 rounded-cf-lg border border-cf-line bg-cf-surface p-5 text-left leading-[normal] shadow-[0_24px_60px_rgba(0,0,0,0.16)]"
      >
        <h2 id={titleId} className="text-[18px] font-semibold tracking-[-0.02em]">
          {t('adminFinance.deposits.refundConfirm.title')}
        </h2>
        <p id={bodyId} className="text-[13px] leading-[1.5] text-cf-ink-muted">
          {t('adminFinance.deposits.refundConfirm.body', { name, amount })}
        </p>
        <div className="mt-1 flex justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            className="h-10 rounded-cf-md border border-cf-line bg-cf-surface px-3.5 text-[13px] font-semibold"
          >
            {t('adminFinance.deposits.refundConfirm.cancel')}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            className="h-10 rounded-cf-md bg-cf-brand px-3.5 text-[13px] font-semibold text-white hover:bg-cf-brand-hover"
          >
            {t('adminFinance.deposits.refundConfirm.confirm', { amount })}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * «Поповнити вручну» — the direct top-up form (SC-5, no client request
 * involved). Records a CHF payment allocated to the deposit through the ONE
 * client-payment endpoint, with a `paymentId` minted once per open. Amount
 * is free-typed; method mirrors the confirm dialog on the requests desk — `bank | cash` only while (q) is unanswered — and a cash
 * entry needs the same cash-desk receipt `ClientPaymentRequestSchema`
 * requires server-side, checked here first so the operator sees it before
 * the round trip.
 */
function DirectTopUpDialog({
  userId,
  name,
  onClose,
}: {
  userId: string;
  name: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const titleId = useId();
  const [amountChf, setAmountChf] = useState('');
  const [method, setMethod] = useState<LedgerMethod>('bank');
  const [receiptRef, setReceiptRef] = useState('');
  // One idempotency key per open: a retry or a double click replays, never credits twice.
  const [paymentId] = useState(() => crypto.randomUUID());
  const amountRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useReturnFocusOnUnmount();
  useEffect(() => {
    amountRef.current?.focus();
  }, []);

  const amount = Number.parseInt(amountChf, 10);
  const amountValid = Number.isFinite(amount) && amount > 0;
  const receiptMissing = method === 'cash' && receiptRef.trim().length === 0;

  const mutation = useMutation({
    mutationFn: () =>
      createDirectTopUp(userId, {
        paymentId,
        amountChf: amount,
        method,
        receiptRef: receiptRef.trim() || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DEPOSITS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: FINANCE_CLIENTS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'finance', 'summary'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'finance', 'journal'] });
      void queryClient.invalidateQueries({ queryKey: ['user', userId] });
      onClose();
    },
  });

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }
    trapTabKey(dialogRef.current, event);
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-cf-ink/35 p-4 backdrop-blur-[2px]"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onKeyDown}
        className="flex w-full max-w-[420px] flex-col gap-3.5 rounded-cf-lg border border-cf-line bg-cf-surface p-5 text-left leading-[normal] shadow-[0_24px_60px_rgba(0,0,0,0.16)]"
      >
        <h2 id={titleId} className="text-[18px] font-semibold tracking-[-0.02em]">
          {t('adminFinance.deposits.directTopUp.title', { name })}
        </h2>

        <label className="flex flex-col gap-1 text-[13px]">
          <span className="text-cf-ink-muted">{t('adminFinance.deposits.directTopUp.amountLabel')}</span>
          <input
            ref={amountRef}
            value={amountChf}
            inputMode="numeric"
            onChange={(event) => setAmountChf(event.target.value.replace(/\D/g, ''))}
            className="h-10 rounded-cf-md border border-cf-line bg-cf-surface px-3 font-mono text-[14px]"
          />
        </label>

        <label className="flex flex-col gap-1 text-[13px]">
          <span className="text-cf-ink-muted">{t('adminFinance.deposits.directTopUp.methodLabel')}</span>
          <select
            value={method}
            onChange={(event) => setMethod(event.target.value as LedgerMethod)}
            className="h-10 rounded-cf-md border border-cf-line bg-cf-surface px-3 text-[14px]"
          >
            {DIRECT_TOPUP_METHODS.map((option) => (
              <option key={option} value={option}>
                {t(`adminFinance.topUp.method.${option}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-[13px]">
          <span className="text-cf-ink-muted">
            {t('adminFinance.deposits.directTopUp.receiptLabel')}
            {method === 'cash'
              ? ' *'
              : ` (${t('adminFinance.deposits.directTopUp.receiptOptional')})`}
          </span>
          <input
            value={receiptRef}
            onChange={(event) => setReceiptRef(event.target.value)}
            aria-invalid={receiptMissing}
            className="h-10 rounded-cf-md border border-cf-line bg-cf-surface px-3 text-[14px]"
          />
          {receiptMissing && (
            <span className="text-[12px] text-cf-danger">
              {t('adminFinance.deposits.directTopUp.receiptRequired')}
            </span>
          )}
        </label>

        {mutation.error && (
          <p role="alert" className="text-[12px] text-cf-danger">
            {financeErrorMessage(mutation.error) ?? t('adminFinance.deposits.directTopUp.failed')}
          </p>
        )}

        <div className="mt-1 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-10 rounded-cf-md border border-cf-line bg-cf-surface px-3.5 text-[13px] font-semibold"
          >
            {t('adminFinance.deposits.directTopUp.cancel')}
          </button>
          <button
            type="button"
            disabled={!amountValid || receiptMissing || mutation.isPending}
            onClick={() => mutation.mutate()}
            className="h-10 rounded-cf-md bg-cf-brand px-3.5 text-[13px] font-semibold text-white disabled:opacity-40"
          >
            {mutation.isPending
              ? t('adminFinance.deposits.directTopUp.pending')
              : t('adminFinance.deposits.directTopUp.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}
